import React, { useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import { useMap, useMapEvents } from 'react-leaflet';
import L from 'leaflet';

// A component that synchronizes map state (zoom, view) between the Leaflet map instance and the parent component's state.
const MapStateSynchronizer = ({
    targetZoom,
    onZoomChange,
    triggerFitBounds,
    activeBounds,
    isolatedModule,
}) => {
    const map = useMap();

    // Effect to update parent's zoom state when the user manually zooms the map.
    useMapEvents({
        zoomend: () => {
            if (map && map.getContainer() && document.body.contains(map.getContainer())) {
                try {
                    const currentMapZoom = map.getZoom();
                    if (currentMapZoom !== targetZoom) {
                        onZoomChange(currentMapZoom);
                    }
                } catch (e) {
                    // Error getting zoom on zoomend is silently ignored.
                }
            }
        }
    });

    // Effect to potentially set map zoom if targetZoom from parent changes, though primarily handled by view changes.
    useEffect(() => {
        if (map && map.getContainer() && typeof targetZoom === 'number' && !isNaN(targetZoom)) {
            try {
                // const currentMapZoom = map.getZoom();
                // if (currentMapZoom !== targetZoom) {
                // map.setZoom(targetZoom); // This line is commented out as per original logic.
                // }
            } catch (e) {
                // Error checking map zoom is silently ignored.
            }
        }
    }, [targetZoom, map]);

    // Callback to fit the map view to the currently active bounds.
    const fitMapToBounds = useCallback(() => {
        if (!map || !map.getContainer() || !document.body.contains(map.getContainer())) {
            return;
        }
        if (activeBounds && typeof activeBounds.isValid === 'function' && activeBounds.isValid()) {
            map.fitBounds(activeBounds, { padding: [20, 20], animate: true, duration: 0.5 });
        }
    }, [activeBounds, map]);

    // Effect to handle changes in map view, such as resetting to full view or focusing on an isolated module.
    useEffect(() => {
        if (map && map.getContainer() && document.body.contains(map.getContainer())) {
            const performViewChange = () => {
                try {
                    if (isolatedModule === null) {
                        if (activeBounds && typeof activeBounds.isValid === 'function' && activeBounds.isValid()) {
                            const calculatedCenter = activeBounds.getCenter();
                            const centerToSet = L.latLng(calculatedCenter.lat, calculatedCenter.lng);
                            const zoomToSet = map.getBoundsZoom(activeBounds);
                            map.setView(centerToSet, zoomToSet, { animate: true, duration: 0.5 });
                        } else {
                            map.setView([0, 0], -4, { animate: true, duration: 0.5 });
                        }
                    } else {
                        fitMapToBounds();
                    }
                } catch (e) {
                    // Error during performViewChange is silently ignored.
                }
            };
            const timer = setTimeout(performViewChange, 0);
            return () => clearTimeout(timer);
        }
    }, [triggerFitBounds, isolatedModule, activeBounds, map, fitMapToBounds]);

    return null;
};

MapStateSynchronizer.propTypes = {
    targetZoom: PropTypes.number.isRequired,
    onZoomChange: PropTypes.func.isRequired,
    triggerFitBounds: PropTypes.number.isRequired,
    activeBounds: PropTypes.instanceOf(L.LatLngBounds),
    isolatedModule: PropTypes.string,
};

export default MapStateSynchronizer;